import { Controller, useForm } from "react-hook-form";
import { CreateOrderRequest, OrderLocalItemResult } from "services/orders/models/OrdersTypes";
import ApplicantSection from "./ApplicantSection";
import Accordion from "components/blocks/Accordion";
import AccordionItem from "components/blocks/Accordion/AccordionItem";
import { useTranslation } from "react-i18next";
import FormInput from "components/blocks/FormInput";
import { useEffect, useImperativeHandle, useRef, useState } from "react";
import { getCreateOrderSchema } from "../createOrder.schema";
import useMutation from "hooks/useMutation";
import { OrdersService } from "services/orders";
import React from "react";
import LookupDropDowns from "./LookupDropDowns";
import RulesAndConditionSection from "./RulesAndConditionSection";
import { ProgramByIDResponse, ProgramDetailsResult } from "services/programs/models/ProgramTypes";
import { useNavigate } from "react-router-dom";
import { OrderStatus } from "shared/lookups";
import FormViewComponent from "../../../../../components/formBuilder/FormViewComponent";
import { AddEditFormRef, OrderFormTypes } from "..";
import Recommendations from "./Recommendations";
import FileUploadWithPreview, { SelectedFilesType } from "components/blocks/FileUploadWithPreview";

const OrderFormDetails = React.forwardRef<
  AddEditFormRef,
  {
    program?: ProgramByIDResponse | null;
    order: OrderLocalItemResult | null;
    serialGuid?: string;
    activityGuid?:string;
    orderFormType: keyof typeof OrderFormTypes;
      isReadonly?: boolean
      isFileReadonly?:boolean
  }
>((props, ref) => {
  const { t, i18n } = useTranslation(["Orders", "Common"]);
  const formRef = useRef<HTMLFormElement>(null);
  const navigate = useNavigate();

  const [files, setFiles] = useState<SelectedFilesType[]>([]);
  const [formSchema, setFormSchema] = useState();
  const {
    formState: { errors },
    handleSubmit,
    register,
    setValue,
    control,
    reset,
    trigger,
  } = useForm<CreateOrderRequest>({
    resolver: getCreateOrderSchema(t,props.program),
    defaultValues: {
      programId: props.program?.id ?? 0,
      isAcceptedConditionAndRules: false,
      isAcceptedCommitments: false,
      filesIds: [],
      formSubject: "",
      formValue: "",
      subspecialty: "",
      isFormValueReady: true,
      isDraft: false,
    },
  });

  const { mutateAsync: addOrderAsync } = useMutation({
    queryFn: async (values: CreateOrderRequest) => {
      return await OrdersService.createOrder(values);
    },
    notificationSuccessMessage: t("orderSubmittedSuccessfully", {
      programSerial: props.program?.serialNumber,
    }),
  });

  const { mutateAsync: updateOrderAsync } = useMutation({
    queryFn: async ({ id, values }: { id: number; values: CreateOrderRequest }) => {
      return await OrdersService.updateOrder(id, values);
    },
    notificationSuccessMessage: t("orderSubmittedSuccessfully", {
      programSerial: props.program?.serialNumber,
    }),
  });

  const onSubmit = async (values: CreateOrderRequest, isDraft: boolean) => {
    values.filesIds = files.map((f) => f.uuid);
    values.isDraft = isDraft;
    
    const response = props.order
      ? await updateOrderAsync({ id: props.order.id!, values })
      : await addOrderAsync(values);

    if (response && !response.hasError) {
      setFiles([]);
      formRef?.current?.classList.remove("was-validated");
      navigate(`/home/orders/researcherOrders/allUnsignedOrders`);
    }
  };

  useImperativeHandle(ref, () => ({
    submitForm: (isDraft: boolean, orderFormType: keyof typeof OrderFormTypes) => {
      handleSubmit((values) => {
        if (orderFormType === OrderFormTypes.ReturnedOrder) {
          values.serialGuid = props.serialGuid;
          values.activityGuid = props.activityGuid;
          values.actionName = "Complete";
        }

        if (orderFormType === OrderFormTypes.EditOrderByDeanOfScientificResearch) {
          values.serialGuid = props.serialGuid;
          values.activityGuid = props.activityGuid;
          values.actionName = "Approve";
        }

        onSubmit(values, isDraft);
        formRef.current?.classList.add("was-validated");
      })();
    },
    validateForm: async (): Promise<boolean> => {
      const isValid = await trigger();
      if (!isValid) {
        // Scroll to first error
        setTimeout(() => {
          const firstError = document.querySelector('.invalid-feedback');
          if (firstError) {
            firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      }
      return isValid;
    },
  }));
  
  useEffect(() => {
    if (props.order) {
      reset({
        ...props.order,
        filesIds: props.order.files.map((f) => f.id),
        isDraft: OrderStatus.Draft === props.order.orderStatusId,
        subspecialty: props.order.subspecialty ?? "Test",
        formValue: props.order.formValue ?? "{}",
        researchFieldId: props.order.researchFieldId ?? 0,
      });

      setFiles(
        props.order?.files.map((f) => ({
          uuid: f.id,
          fileName: f.name ?? "",
          id: f.id,
          size: f.size,
          file: new File([], f.name ?? ""),
          createdBy: f.createdBy,
          createdOn: f.createdOn, 
          showDeleteButton: (!props.isReadonly && !props.isFileReadonly)
        })) ?? [],
      );
    }

    if (props.program?.id) {
      setValue("programId", props.program?.id);
    }
  }, [props.order, props.program?.id, props.isReadonly, props.isFileReadonly, reset, setValue]);

  useEffect(() => {
    const formSchema = JSON.parse(props.program?.formBuilderSchema ?? "{}") as any;
    setFormSchema(formSchema);
  }, [props.program?.formBuilderSchema]);
  
  const getDetails = ():ProgramDetailsResult=>{
   return  props.program?.programDetails.find(
        (item) => item.locale?.toLowerCase() === i18n.language,
    ) as ProgramDetailsResult
  }



  return (
    <form ref={formRef} noValidate onSubmit={handleSubmit((values) => onSubmit(values, false))}>
      <ApplicantSection />
      {props.order && OrderFormTypes.ReturnedOrder === props.orderFormType && <Recommendations order={props.order} />}
      <Accordion>
        <AccordionItem initiallyOpen title={t("submissionForm")} elementId="submissionForm">
          <div className="d-flex flex-column gap-3 pb-2 border-bottom ">
            {props.program ==null &&   <FormInput
              label={t("formSubject")}
              labelClassName="fw-bold"
              placeholder={t("formSubject")}
              withAsterisk
              {...register("formSubject")}
              readOnly={props.isReadonly}
              error={errors.formSubject?.message}
            />}
            {props.program  && !props.program.isFromSubjectHidden &&   <FormInput
              label={getDetails().fromSubjectLabel?? t("formSubject")}
              labelClassName="fw-bold"
              placeholder={getDetails().fromSubjectLabel??t("formSubject")}
              withAsterisk = {props.program.isFromSubjectRequired ?? true}
              {...register("formSubject")}
              readOnly={props.isReadonly}
              error={errors.formSubject?.message}
            />}

            <div>
              <Controller
                name="formValue"
                control={control}
                render={({ field }) => (
                  <FormViewComponent
                    data={field.value ? JSON.parse(field.value) : {}}
                    formDefinition={formSchema}
                    readOnly={props.isReadonly}
                    onChange={(values) => {
                      setValue("isFormValueReady", values.isValid);
                      setValue("formValue", JSON.stringify(values.data));
                    }}
                  />
                )}
              />

              {errors.isFormValueReady?.message && (
                <span className="invalid-feedback my-2" style={{ display: "block" }}>
                  {errors.isFormValueReady?.message}
                </span>
              )}
            </div>
            <LookupDropDowns
              program={props?.program}
              details={getDetails()}
              isReadonly={props.isReadonly}
              control={control}
              errors={errors}
              setValue={setValue}
              order={props.order}
              isInspectMode={props.order?.isInspectMode}
            />
            {  props.program ==null &&  <FileUploadWithPreview 
              label={t("ResearchAttachments")}
              files={files}
              setFiles={setFiles}
              formProps={{}}
              // AddOnly={props.isFileReadonly}
              error={errors.filesIds?.message}
              setValue={setValue}
              uploadWithUser={true}
            />} 
            
            {  !props.program?.isFileAttachmentHidden &&  <FileUploadWithPreview 
              label={!getDetails()? t("ResearchAttachments") : getDetails().fileAttachmentLabel}
              files={files}
              setFiles={setFiles}
              withAsterisk={props.program?.isFileAttachmentRequired?? false}
              formProps={{}}
                // AddOnly={props.isFileReadonly}
              error={errors.filesIds?.message}  
              setValue={setValue}
              uploadWithUser={true}
            />}
          </div>
        </AccordionItem>
      </Accordion>

      <RulesAndConditionSection
        isReadonly={props.isReadonly}
        register={register}
        errors={errors}
        programDetails={
          props.program?.programDetails.find(
            (item) => item.locale?.toLowerCase() === i18n.language,
          ) as ProgramDetailsResult
        }
      />
    </form>
      
  );
});

export default OrderFormDetails;
